(function () {
  'use strict';

  class BundleBuilder {
    constructor(root) {
      this.root = root;
      this.requiredCount = parseInt(root.dataset.productsRequired, 10) || 3;
      this.currencySymbol = root.dataset.currencySymbol || '';

      // Parsed once: [{ id, price_cents, available }, ...] for the bundle
      // product's own variants. We pick the one whose price matches the sum
      // of whatever fragrances were selected.
      this.bundleVariants = [];
      try {
        this.bundleVariants = JSON.parse(root.dataset.bundleVariants || '[]');
      } catch (e) {
        this.bundleVariants = [];
      }

      this.grid = root.querySelector('[data-product-grid]');
      this.searchInput = root.querySelector('[data-search-input]');
      this.summaryList = root.querySelector('[data-summary-list]');
      this.progressText = root.querySelector('[data-progress-text]');
      this.totalText = root.querySelector('[data-total-text]');
      this.addBundleBtn = root.querySelector('[data-add-bundle-btn]');
      this.errorMessage = root.querySelector('[data-error-message]');
      this.noResults = root.querySelector('[data-no-results]');

      // selected = [{ variantId, title, size, priceCents }]
      // One entry per unit — a fragrance picked twice appears as two
      // entries here, each occupying its own summary slot, just like two
      // different fragrances would. This keeps all the slot/summary/total
      // logic below unchanged; only *how* entries get added/removed
      // (via a quantity stepper instead of a single toggle) has changed.
      this.selected = [];

      this.bindEvents();
      this.renderSummary();
    }

    bindEvents() {
      if (this.grid) {
        this.grid.addEventListener('click', (e) => {
          const increaseBtn = e.target.closest('[data-qty-increase]');
          const decreaseBtn = e.target.closest('[data-qty-decrease]');
          if (!increaseBtn && !decreaseBtn) return;

          const card = e.target.closest('[data-product-card]');
          if (!card) return;

          if (increaseBtn) this.handleQtyIncrease(card);
          if (decreaseBtn) this.handleQtyDecrease(card);
        });
      }

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => this.handleSearch());
      }

      if (this.summaryList) {
        this.summaryList.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-remove-btn]');
          if (!btn) return;
          const slotIndex = parseInt(btn.dataset.slotIndex, 10);
          this.removeAtSlot(slotIndex);
        });
      }

      if (this.addBundleBtn) {
        this.addBundleBtn.addEventListener('click', () => this.addBundleToCart());
      }
    }

    handleSearch() {
      const term = this.searchInput.value.trim().toLowerCase();
      const cards = this.grid.querySelectorAll('[data-product-card]');
      let visibleCount = 0;

      cards.forEach((card) => {
        const searchTerm = card.dataset.searchTerm || '';
        const matches = searchTerm.indexOf(term) !== -1;
        card.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      if (this.noResults) {
        this.noResults.hidden = visibleCount !== 0;
      }
    }

    // Reflects a card's current quantity in its stepper UI and
    // is-selected styling. Called after every increase/decrease.
    updateCardQtyUI(card, qty) {
      card.dataset.qty = qty;
      card.classList.toggle('is-selected', qty > 0);

      const qtyValue = card.querySelector('[data-qty-value]');
      if (qtyValue) qtyValue.textContent = qty;

      const decreaseBtn = card.querySelector('[data-qty-decrease]');
      if (decreaseBtn) decreaseBtn.disabled = qty <= 0;
    }

    handleQtyIncrease(card) {
      if (card.dataset.available === 'false') return;

      if (this.selected.length >= this.requiredCount) {
        this.showError(
          'You have already selected ' + this.requiredCount + '. Remove one to add another.'
        );
        return;
      }

      this.clearError();

      const variantId = card.dataset.variantId;
      this.selected.push({
        variantId: variantId,
        title: card.dataset.title,
        size: card.dataset.size,
        priceCents: parseInt(card.dataset.price, 10) || 0,
      });

      const newQty = parseInt(card.dataset.qty, 10) + 1;
      this.updateCardQtyUI(card, newQty);
      this.renderSummary();
    }

    handleQtyDecrease(card) {
      const currentQty = parseInt(card.dataset.qty, 10) || 0;
      if (currentQty <= 0) return;

      const variantId = card.dataset.variantId;

      // Remove the last-added entry for this variant (not necessarily the
      // last entry overall, since other products may have been added since).
      let indexToRemove = -1;
      for (let i = this.selected.length - 1; i >= 0; i -= 1) {
        if (this.selected[i].variantId === variantId) {
          indexToRemove = i;
          break;
        }
      }
      if (indexToRemove === -1) return;

      this.selected.splice(indexToRemove, 1);

      this.updateCardQtyUI(card, currentQty - 1);
      this.clearError();
      this.renderSummary();
    }

    removeAtSlot(slotIndex) {
      const removed = this.selected[slotIndex - 1];
      if (!removed) return;

      this.selected.splice(slotIndex - 1, 1);

      // Recompute how many units of this variant remain selected, since
      // the same product may appear in multiple slots.
      const remainingQty = this.selected.filter(
        (item) => item.variantId === removed.variantId
      ).length;

      const card = this.grid.querySelector(
        '[data-product-card][data-variant-id="' + removed.variantId + '"]'
      );
      if (card) {
        this.updateCardQtyUI(card, remainingQty);
      }

      this.clearError();
      this.renderSummary();
    }

    // Sum of the selected fragrances' real prices, in cents.
    getSelectedTotalCents() {
      return this.selected.reduce((sum, item) => sum + (item.priceCents || 0), 0);
    }

    // Flat-rate discount by bundle size. Add more tiers here if you
    // introduce new bundle sizes later.
    getDiscountRate() {
      const rates = { 3: 0.30, 4: 0.40, 6: 0.50 };
      return rates[this.requiredCount] || 0;
    }

    // The price actually charged: raw sum minus the tier discount,
    // rounded to the nearest cent. This is the number matched against
    // the bundle product's variants and the number added to cart.
    getDiscountedTotalCents() {
      const rawCents = this.getSelectedTotalCents();
      const rate = this.getDiscountRate();
      return Math.round(rawCents * (1 - rate));
    }

    formatCents(cents) {
      return this.currencySymbol + (cents / 100).toFixed(2);
    }

    // Finds the bundle product's variant whose price exactly matches the
    // sum of the selected fragrances. Returns null if none matches.
    findMatchingBundleVariant(totalCents) {
      return (
        this.bundleVariants.find(
          (v) => v.price_cents === totalCents && v.available !== false
        ) || null
      );
    }

    renderSummary() {
      if (this.summaryList) {
        let html = '';
        for (let i = 1; i <= this.requiredCount; i += 1) {
          const item = this.selected[i - 1];
          html += this.summaryItemMarkup(i, item);
        }
        this.summaryList.innerHTML = html;
      }

      if (this.progressText) {
        this.progressText.textContent = this.selected.length + ' / ' + this.requiredCount + ' Selected';
      }

      const isComplete = this.selected.length === this.requiredCount;
      const rawCents = this.getSelectedTotalCents();
      const discountedCents = this.getDiscountedTotalCents();
      const rate = this.getDiscountRate();

      if (this.totalText) {
        if (isComplete) {
          if (rate > 0) {
            this.totalText.innerHTML =
              'Total: <s class="bundle-builder__total-original">' + this.formatCents(rawCents) + '</s> ' +
              '<span class="bundle-builder__total-discounted">' + this.formatCents(discountedCents) + '</span> ' +
              '<span class="bundle-builder__total-savings">(' + Math.round(rate * 100) + '% off)</span>';
          } else {
            this.totalText.textContent = 'Total: ' + this.formatCents(discountedCents);
          }
          this.totalText.hidden = false;
        } else {
          this.totalText.hidden = true;
        }
      }

      if (this.addBundleBtn) {
        this.addBundleBtn.disabled = !isComplete;
        this.addBundleBtn.textContent = isComplete
          ? 'Add Bundle - ' + this.formatCents(discountedCents)
          : 'Add Bundle';
      }
    }

    summaryItemMarkup(slotIndex, item) {
      if (item) {
        return (
          '<li class="bundle-summary-item is-filled" data-summary-slot="' + slotIndex + '">' +
            '<span class="bundle-summary-item__icon" aria-hidden="true">&#10004;</span>' +
            '<span class="bundle-summary-item__label">' +
              this.escapeHtml(item.title) +
              ' <span class="bundle-summary-item__size">(' + this.escapeHtml(item.size) + ')</span>' +
            '</span>' +
            '<span class="bundle-summary-item__price">' + this.formatCents(item.priceCents) + '</span>' +
            '<button type="button" class="bundle-summary-item__remove" data-remove-btn data-slot-index="' + slotIndex + '" aria-label="Remove ' + this.escapeHtml(item.title) + '">&#10005;</button>' +
          '</li>'
        );
      }

      return (
        '<li class="bundle-summary-item" data-summary-slot="' + slotIndex + '">' +
          '<span class="bundle-summary-item__icon" aria-hidden="true">&#9675;</span>' +
          '<span class="bundle-summary-item__label">Add Product ' + slotIndex + '</span>' +
        '</li>'
      );
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }

    clearError() {
      if (!this.errorMessage) return;
      this.errorMessage.hidden = true;
      this.errorMessage.textContent = '';
    }

    async addBundleToCart() {
      if (this.selected.length !== this.requiredCount) return;

      const rawCents = this.getSelectedTotalCents();
      const discountedCents = this.getDiscountedTotalCents();
      const matchedVariant = this.findMatchingBundleVariant(discountedCents);

      if (!matchedVariant) {
        this.showError(
          'This combination totals ' + this.formatCents(rawCents) +
          ', which discounts to ' + this.formatCents(discountedCents) +
          ', but the bundle product has no variant at that price yet. ' +
          'Add a ' + this.formatCents(discountedCents) + ' variant to the bundle product in Shopify Admin.'
        );
        return;
      }

      this.addBundleBtn.disabled = true;
      this.addBundleBtn.textContent = 'Adding...';

      const properties = {};
      this.selected.forEach((item, i) => {
        const n = i + 1;
        properties['Fragrance ' + n] = item.title + ' (' + item.size + ')';
        properties['_Variant ID ' + n] = item.variantId;
      });

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: matchedVariant.id,
                quantity: 1,
                properties: properties,
              },
            ],
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || 'Could not add bundle to cart.');
        }

        this.resetSelection();
        await this.openCartDrawer();
      } catch (err) {
        this.showError(err.message || 'Something went wrong adding the bundle.');
      } finally {
        this.renderSummary();
      }
    }

    resetSelection() {
      this.selected = [];
      this.grid.querySelectorAll('[data-product-card]').forEach((card) => {
        this.updateCardQtyUI(card, 0);
      });
      this.renderSummary();
    }

    async openCartDrawer() {
      // Let any theme-level listeners know the cart changed.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      // Common Dawn/Sense-style cart drawer custom element.
      const drawer = document.querySelector('cart-drawer, #CartDrawer, cart-notification');
      if (drawer) {
        try {
          const sectionsResponse = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');
          if (sectionsResponse.ok) {
            const sections = await sectionsResponse.json();
            document.dispatchEvent(
              new CustomEvent('cart:updated', { bubbles: true, detail: { sections: sections } })
            );
          }
        } catch (e) {
          // Section fetch failed silently; cart:refresh listeners can still handle it.
        }

        if (typeof drawer.open === 'function') {
          drawer.open();
        } else {
          drawer.classList.add('active', 'is-open', 'drawer--open');
        }
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-bundle-builder]').forEach((root) => {
      new BundleBuilder(root);
    });
  });
})();
